/**
 * VaultAccessGrantSheet — host-rendered pick-and-consent flow behind the
 * `vault_documents_pick` bridge op (the purchase-confirmation-sheet pattern
 * applied to user data; sanctioned transport exception, see
 * docs/capability-placement.md § "Transport exceptions").
 *
 * Two steps, both host chrome the plugin can neither see nor script:
 *   1. the shared Vault picker (multi-select; uploads off; proprietary
 *      documents filtered out — the R2 mirror, also enforced server-side),
 *   2. an explicit consent confirmation that is HONEST about what a grant
 *      means: standing access until revoked, apps can copy what you share,
 *      access is logged, revocation lets in-flight links live out ≤1 h.
 *
 * Confirm creates the per-document grants via the host session
 * (POST /v1/vault/consents/arche — the plugin can never mint its own), then
 * resolves the plugin's promise with picked-document METADATA only; content
 * flows through the audited consented-read entity verbs afterwards.
 *
 * EDIT ACCESS (`access: 'write'`). The same sheet, asking a bigger question:
 * the app may also SAVE CHANGES to what is picked. The consent step says so in
 * its title and spells out what makes that tolerable — earlier versions are
 * kept, the file shows which app changed it, and access can be removed at any
 * time. It is never the default and never inferred: the plugin has to ask for
 * it, and the server refuses it for an app that may not edit user files.
 *
 * UPGRADE (`documentId`). An editor usually learns it needs edit access AFTER
 * the user opened a file with it (a read grant, often from a handoff with no
 * sheet at all). Sending the user back to a file picker to re-find the file
 * they are looking at would be absurd, so the plugin may name ONE document —
 * and the host skips the picker for it ONLY IF the app already holds an active
 * grant on that document. Otherwise the id is ignored and the ordinary picker
 * opens, so naming an id can never be used to probe whether a document exists
 * or to put a file the user did not choose on the consent screen.
 */
import { useCallback, useEffect, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, FileText, ShieldCheck, PencilLine } from 'lucide-react';
import { useConsentArmDelay } from '@/lib/hooks/useConsentArmDelay';
import { OpenFileDialog } from '@/components/files/file-dialog/OpenFileDialog';
import { scrubForDisplay } from '@/components/files/file-dialog/display-safety';
import type { FileAccept } from '@/components/files/file-dialog/useFileBrowser';
import { useFileDialogWorkspaces } from '@/components/files/file-dialog/useFileDialogData';
import type { VaultFileDialogDocumentDto } from '@/lib/api';
import { invalidateVaultDocumentsAfterHostWrite } from './vaultHostWrites';

export interface VaultPickResolvedDocument {
  documentId: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number | null;
  /** What the user granted for this document. */
  access: 'read' | 'write';
}

export type VaultPickResolution = { canceled: true } | { documents: VaultPickResolvedDocument[] };

interface VaultAccessGrantSheetProps {
  open: boolean;
  /** The plugin arche requesting access. */
  archeId: string;
  /** Display name for the consent copy; fetched by the parent when known. */
  archeName: string | null;
  maxDocuments: number;
  /** `write` = also let the app save changes. Default `read`. */
  access?: 'read' | 'write';
  /** Upgrade path: a document the app ALREADY holds a grant on (see header). */
  documentId?: string;
  /**
   * The workspace being shared FROM — the host's acting Holder — when it is
   * an org workspace; null / omitted = the user's Personal Holder. Drives the
   * PICKER (which files are offered). The grant itself is made on whichever
   * workspace the picked documents live in, resolved server-side.
   */
  holderId?: string | null;
  /** Display name of that workspace, for the consent copy. */
  holderLabel?: string | null;
  /** The types the arche can open (`pick({ accept })`). */
  accept?: FileAccept;
  onResolve: (result: VaultPickResolution) => void;
}

type Step = 'resolving' | 'pick' | 'confirm';

/** What the consent step needs of a document — from the picker, or from the
 *  host's own lookup on the upgrade path. */
interface SelectedDocument {
  documentId: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number;
  /** The workspace the file lives in — the grant is made there. */
  holderId: string;
}

export function VaultAccessGrantSheet({
  open,
  archeId,
  archeName,
  maxDocuments,
  access = 'read',
  documentId,
  holderId = null,
  holderLabel = null,
  accept,
  onResolve,
}: VaultAccessGrantSheetProps) {
  const { workspaces } = useFileDialogWorkspaces('read');
  // The host mounts a FRESH sheet per request (keyed), so initial state is the
  // whole reset story — nothing from a previous request can be painted under
  // this one's consent copy. On the upgrade path start at `resolving`, never
  // at the picker the user is about to skip.
  const [step, setStep] = useState<Step>(documentId ? 'resolving' : 'pick');
  const [selected, setSelected] = useState<SelectedDocument[]>([]);
  /** True when `selected` came from the upgrade path, not the picker. */
  const [preselected, setPreselected] = useState(false);
  const [granting, setGranting] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setPreselected(false);
    setGranting(false);
    setGrantError(null);
    if (!documentId) {
      setStep('pick');
      return;
    }

    // Upgrade path — only for a document this app can ALREADY read.
    let cancelled = false;
    setStep('resolving');
    (async () => {
      try {
        // The grant lives on the DOCUMENT's workspace, which need not be the
        // one the user is acting in (a file opened by handoff, say) — so the
        // document is looked up first and its grants are listed there.
        const { document } = await api.getVaultDocument(documentId);
        const { consents } = await api.listVaultArcheConsents({
          archeId,
          documentId,
          holderId: document.holderId,
        });
        const held = consents.some(
          (c) =>
            c.status === 'active' &&
            // The server already filters by `archeId`; checked here too so
            // that skipping the picker never rests on a query parameter.
            c.granteeArcheId === archeId &&
            c.scopeJson.documentId === documentId,
        );
        if (!held) throw new Error('not granted');
        if (cancelled) return;
        setSelected([
          {
            documentId: document.documentId,
            fileName: document.name,
            mimeType: document.mimeType ?? null,
            sizeBytes: document.sizeBytes,
            holderId: document.holderId,
          },
        ]);
        setPreselected(true);
        setStep('confirm');
      } catch {
        // Not granted, gone, or unreadable: say nothing about why, and fall
        // back to the ordinary picker.
        if (!cancelled) setStep('pick');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, archeId, documentId]);

  const appLabel = archeName ?? 'This arche';
  const wantsWrite = access === 'write';
  // Edit access over a file that keeps no earlier versions lets the arche
  // overwrite it with no way back — the user is told before they allow it
  // (file areas §4.12). Only asked for the files on the consent step.
  const historyQueries = useQueries({
    queries: selected.map((file) => ({
      queryKey: queryKeys.vaultDocuments.history(file.documentId),
      queryFn: () => api.getVaultDocumentHistory(file.documentId),
      enabled: open && wantsWrite && step === 'confirm',
      retry: false,
    })),
  });
  const withoutHistory = selected.filter(
    (_file, i) => historyQueries[i]?.data?.effective.keep === false,
  );
  // Re-armed per step: "Allow" must not be clickable the instant the consent
  // step replaces the picker (or the "checking" state) under the cursor.
  const armed = useConsentArmDelay(step);

  const handleFilesSelected = useCallback((files: VaultFileDialogDocumentDto[]) => {
    setSelected(
      files.map((f) => ({
        documentId: f.documentId,
        fileName: f.name,
        mimeType: f.mimeType ?? null,
        sizeBytes: f.sizeBytes,
        holderId: f.holderId,
      })),
    );
    setPreselected(false);
    setStep('confirm');
  }, []);

  const handleCancel = useCallback(() => {
    onResolve({ canceled: true });
  }, [onResolve]);

  const handleConfirm = useCallback(async () => {
    setGranting(true);
    setGrantError(null);
    const granted = selected.slice(0, maxDocuments);
    try {
      await api.grantVaultArcheConsent({
        granteeArcheId: archeId,
        documentIds: granted.map((f) => f.documentId),
        // Sent ONLY when the user was shown the "and change" consent.
        ...(wantsWrite ? { access: 'write' as const } : {}),
      });
      // Fias Files' "Arche access" list and the Arche Access page must show this
      // grant now, not after their cache goes stale.
      invalidateVaultDocumentsAfterHostWrite();
      onResolve({
        documents: granted.map((f) => ({
          documentId: f.documentId,
          name: f.fileName,
          mimeType: f.mimeType ?? null,
          sizeBytes: f.sizeBytes ?? null,
          access,
        })),
      });
    } catch (err) {
      const error = err as Error & { code?: string };
      setGrantError(
        error.code === 'SENSITIVITY_FORBIDDEN_FOR_ARCHE_GRANT'
          ? 'One of the selected documents is marked Proprietary — proprietary documents cannot be shared with arches. Go back and deselect it.'
          : error.code === 'ARCHE_CANNOT_EDIT'
            ? `${appLabel} isn’t allowed to edit your files.`
            : (error.message ?? 'Could not grant access. Please try again.'),
      );
    } finally {
      setGranting(false);
    }
  }, [access, appLabel, archeId, maxDocuments, onResolve, selected, wantsWrite]);

  // The host mounts this sheet only while a pick is in flight (and keyed per
  // request), so `open` is normally true. The guard stays for any other host:
  // an always-mounted picker dialog would run its queries with no pick pending.
  if (!open) return null;

  const count = selected.length;
  const plural = count !== 1 ? 's' : '';
  // Where the files come from, named so the user sees WHOSE documents an arche
  // is being given: "your Vault" for Personal, the workspace by name otherwise.
  // Before the pick that is the acting workspace; after it, the workspace the
  // chosen files actually live in (the user may have switched in the dialog).
  const pickedWorkspace = workspaces.find((w) => w.holderId === selected[0]?.holderId);
  const grantHolderLabel = pickedWorkspace
    ? pickedWorkspace.holderType === 'personal'
      ? null
      : pickedWorkspace.label
    : holderLabel;
  const sourceLabel = holderLabel ? `the ${holderLabel} workspace` : 'your Vault';

  return (
    <>
      <OpenFileDialog
        open={open && step === 'pick'}
        onOpenChange={(o) => {
          if (!o) handleCancel();
        }}
        multiSelect
        selectionLimit={maxDocuments}
        onConfirm={handleFilesSelected}
        // Opens on the acting workspace; the user (never the arche) may switch.
        // The grant lands on whichever workspace the chosen files live in.
        initialHolderId={holderId}
        title={
          wantsWrite ? `Choose documents ${appLabel} can edit` : `Share documents with ${appLabel}`
        }
        description={
          wantsWrite
            ? `Pick up to ${maxDocuments} document${maxDocuments !== 1 ? 's' : ''} from ${sourceLabel}. ${appLabel} will be able to read and change only what you pick here.`
            : `Pick up to ${maxDocuments} document${maxDocuments !== 1 ? 's' : ''} from ${sourceLabel}. ${appLabel} will only be able to read what you pick here.`
        }
        confirmLabel="Continue"
        // Uploads are for the user's own flows, not an arche's ask — the
        // consent decision is about EXISTING documents.
        allowUpload={false}
        // Only what an arche may ever be given, capped by the user's role
        // (never proprietary) — filtered server-side, so a file the user
        // could not grant is never offered.
        forArche
        accept={accept}
        memoryKey={archeId}
        accessLine={
          wantsWrite
            ? `${appLabel} is asking to view and edit the files you choose.`
            : `${appLabel} is asking to view the files you choose.`
        }
      />

      <Dialog
        open={open && step !== 'pick'}
        onOpenChange={(o) => {
          if (!o && !granting) handleCancel();
        }}
      >
        <DialogContent className="max-w-md">
          {step === 'resolving' ? (
            <>
              {/* A dialog with no title is unlabelled to a screen reader. */}
              <DialogHeader className="sr-only">
                <DialogTitle>Checking the file</DialogTitle>
                <DialogDescription>
                  {appLabel} asked for access to a file. Checking it before asking you.
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Checking the file…
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {wantsWrite ? (
                    <PencilLine className="h-5 w-5 text-primary" aria-hidden="true" />
                  ) : (
                    <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
                  )}
                  {wantsWrite
                    ? `Allow ${appLabel} to edit ${count} document${plural}?`
                    : `Allow ${appLabel} to read ${count} document${plural}?`}
                </DialogTitle>
                <DialogDescription>
                  {grantHolderLabel
                    ? `These documents belong to the ${grantHolderLabel} workspace. `
                    : ''}
                  {wantsWrite
                    ? `${appLabel} will be able to read these documents and save changes to them, until you remove access in Vault → Arche Access. Arches can copy or store content you share with them. Every access and every change is logged.`
                    : `${appLabel} will be able to read these documents until you remove access in Vault → Arche Access. Arches can copy or store content you share with them. Every access is logged.`}
                </DialogDescription>
              </DialogHeader>

              <ul className="max-h-48 overflow-y-auto space-y-1.5 rounded-md border p-3">
                {selected.map((file) => (
                  <li key={file.documentId} className="flex items-center gap-2 text-sm">
                    <FileText
                      className="h-4 w-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    {/* A file name can be app-authored (a promoted file keeps
                        the name its app gave it). Scrubbed so bidi/control
                        characters cannot restyle the consent screen. */}
                    <span className="truncate" title={scrubForDisplay(file.fileName, 255)}>
                      {scrubForDisplay(file.fileName, 120) || 'Untitled file'}
                    </span>
                  </li>
                ))}
              </ul>

              {wantsWrite && withoutHistory.length > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {withoutHistory.length === selected.length
                    ? selected.length === 1
                      ? 'Earlier versions of this file won’t be kept'
                      : 'Earlier versions of these files won’t be kept'
                    : `Earlier versions of ${withoutHistory.length} of these files won’t be kept`}
                  , so changes {appLabel} saves can’t be undone. You can turn version history on
                  from the file’s page.
                </p>
              )}
              {wantsWrite && withoutHistory.length < selected.length && (
                <p className="text-xs text-muted-foreground">
                  {withoutHistory.length > 0 ? 'For the others, your' : 'Your'} earlier versions are
                  kept, so you can go back — they count toward your storage. Each file shows which
                  arche last changed it.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {wantsWrite
                  ? 'Removing access stops new reads and saves immediately. Download links issued in the last hour may keep working until they expire.'
                  : 'Removing access stops new reads immediately. Download links issued in the last hour may keep working until they expire.'}
              </p>

              {grantError && (
                <p className="text-sm text-destructive" role="alert">
                  {grantError}
                </p>
              )}

              <DialogFooter>
                {!preselected && (
                  <Button variant="outline" onClick={() => setStep('pick')} disabled={granting}>
                    Back
                  </Button>
                )}
                <Button variant="outline" onClick={handleCancel} disabled={granting}>
                  Cancel
                </Button>
                <Button onClick={handleConfirm} disabled={granting || !armed || count === 0}>
                  {granting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden="true" />
                  ) : null}
                  {wantsWrite ? 'Allow editing' : 'Allow access'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
